import { useEffect, useRef } from 'react';
import { usePlayer } from '../context/PlayerContext';
import { toast, Toaster } from 'sonner';

/**
 * On the FIRST user interaction anywhere in the app (click / touch / key),
 * if nothing is playing AND no other flow has kicked off a song, quietly
 * start a random one. Uses a ref to read the latest currentSong so a song
 * that arrived after mount from another flow (e.g. Onboarding) is honoured.
 */
const AutoPlayHook = () => {
  const { currentSong, playRandom } = usePlayer();
  const attempted = useRef(false);
  const songRef = useRef(currentSong);

  // Always keep latest currentSong reachable inside the async listener.
  useEffect(() => { songRef.current = currentSong; }, [currentSong]);

  useEffect(() => {
    if (attempted.current) return;
    // If a song is already playing (e.g. Onboarding kicked one off), never attempt.
    if (currentSong) {
      attempted.current = true;
      return;
    }
    const start = () => {
      if (attempted.current) return;
      attempted.current = true;
      cleanup();
      // Re-check the LATEST currentSong via ref — the interaction may have
      // happened while a prior playRandom was resolving.
      if (songRef.current) return;
      // Onboarding (or another flow) has already fired a playRandom whose
      // response hasn't landed in state yet. Don't fire a second one.
      try {
        if (sessionStorage.getItem('pkp_autoplay_lock') === '1') return;
      } catch (e) { /* noop */ }
      playRandom({ expand: false })
        .then((song) => {
          if (song) {
            toast('Pehla gaana baj raha hai', {
              description: `${song.title} — swipe or tap next to change`,
              duration: 5000,
            });
          }
        })
        .catch(() => {});
    };
    const cleanup = () => {
      document.removeEventListener('pointerdown', start);
      document.removeEventListener('keydown', start);
      document.removeEventListener('touchstart', start);
    };
    document.addEventListener('pointerdown', start, { once: true, passive: true });
    document.addEventListener('touchstart', start, { once: true, passive: true });
    document.addEventListener('keydown', start, { once: true });
    return cleanup;
  }, [currentSong, playRandom]);

  return null;
};

export const AutoPlayToaster = () => <Toaster theme="dark" richColors position="bottom-center" />;

export default AutoPlayHook;
